import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendarAlt, FaClock } from 'react-icons/fa';
// import { automationVsAI2026Content } from './BlogPost_AutomationVsAI';
import { developersResistAIContent } from './BlogPost_DevelopersResistAI';

const BlogPost = () => {
  const { slug } = useParams();

  // Blog posts data (same as in Blog.jsx - could be moved to a shared file)
  const allBlogPosts = [
    {
      id: 'will-ai-replace-software-developers',
      icon: '🛡️',
      date: 'Aug 7, 2026',
      readTime: '5 min read',
      title: 'Will AI Replace Software Developers? The Truth About the Future of Coding',
      description: 'AI is changing how software gets built—but replacing repetitive tasks is not the same as replacing engineers. Here is what developers and leaders need to understand.'
    },
    /* Temporarily hidden until the article is ready to publish.
    {
      id: 'automation-vs-ai-2026',
      icon: '🤖',
      date: 'Aug 7, 2026',
      readTime: '10 min read',
      title: 'Automation vs AI in 2026: How to Choose Without the Hype',
      description: 'A practical decision framework for technical leaders navigating the noise between traditional automation and AI—when to use each, and how to avoid expensive mistakes.'
    },
    */
    {
      id: 'business-goals-technical-architecture',
      icon: '🧩',
      date: 'Oct 15, 2025',
      readTime: '8 min read',
      title: 'The Art of Translating Business Goals into Technical Architecture',
      description: 'Bridging the gap between vision and execution through strategic technical architecture, alignment, and empathy-driven design decisions.'
    },
    {
      id: 'software-engineer-to-project-manager',
      icon: '🚀',
      date: 'Oct 12, 2025',
      readTime: '7 min read',
      title: 'From Software Engineer to Project Manager — My Career Transition Story',
      description: 'A personal journey from writing code to leading teams, and discovering that the most complex systems are built in people, communication, and alignment.'
    }
  ];

  // Get other blog posts (excluding current one)
  const otherBlogPosts = allBlogPosts.filter(post => post.id !== slug).slice(0, 1);
  // This could be expanded to fetch blog posts from a CMS or API
  const blogPosts = {
    'will-ai-replace-software-developers': {
      icon: '🛡️',
      date: 'Aug 7, 2026',
      readTime: '5 min read',
      author: 'Itrat Zehra Jassani — Senior Project Manager | Data Science & Software Engineering Background',
      title: 'Will AI Replace Software Developers? The Truth About the Future of Coding',
      heroImage: '/images/will-ai-replace-developers-hero-v2.webp',
      heroAlt: 'Developer designing software architecture while automation handles routine code and security checks',
      content: developersResistAIContent
    },
    /* Temporarily hidden until the article is ready to publish.
    'automation-vs-ai-2026': {
      icon: '🤖',
      date: 'Aug 7, 2026',
      readTime: '10 min read',
      author: 'Itrat Zehra Jassani — Senior Project Manager | Data Science & Software Engineering Background',
      title: 'Automation vs AI in 2026: How to Choose Without the Hype',
      content: automationVsAI2026Content
    },
    */
    'business-goals-technical-architecture': {
      icon: '🧩',
      date: 'Oct 15, 2025',
      readTime: '8 min read',
      author: 'Itrat Zehra Jassani — Senior Project Manager & Software Engineer',
      title: 'The Art of Translating Business Goals into Technical Architecture',
      content: `
        <div class="blog-content-wrapper">
          <h2>🎯 The Bridge Between Vision and Execution</h2>
          
          <p>Every successful digital product begins with an idea — a business goal, a vision, a "what if we could…" moment. But the real magic happens when that vision is translated into something tangible: a system, a process, a structure that actually works.</p>
          
          <p>Over the years, working across healthcare, fintech, and enterprise platforms, I've learned that technical architecture is the language through which business strategy becomes reality. It's not just about frameworks or databases — it's about alignment, empathy, and clarity.</p>
          
          <h2>🧠 Step 1 – Understanding the Why Before the How</h2>
          
          <p>In every kickoff meeting, I ask one simple question:</p>
          
          <blockquote>"What business outcome do we want to see once this system is live?"</blockquote>
          
          <p>It sounds simple, but it reframes the entire discussion. Instead of diving into tech stacks, the conversation shifts toward impact — compliance accuracy, faster onboarding, data transparency, or operational efficiency.</p>
          
          <p>For instance, when leading a logistics digitization project, clarifying that the true goal was "real-time shipment visibility for customers" — not "a new tracking dashboard" — changed the entire architecture approach. Technology then became a tool to serve purpose, not the other way around.</p>
          
          <h2>🏗️ Step 2 – Breaking Down Goals into Logical Blocks</h2>
          
          <p>Every architecture is a story made of smaller chapters.</p>
          
          <p>Imagine you're designing a claims-processing platform in healthcare. The goal isn't to "build a claim form." The goal is to reduce approval time and eliminate manual review errors. That single statement should guide every architectural decision — from form modularity and automation triggers to data-validation rules and audit logging.</p>
          
          <p>I usually translate a goal into three structural layers:</p>
          
          <ul>
            <li><strong>Experience Layer (UX / Workflow):</strong> How should users feel while completing their task?</li>
            <li><strong>Logic Layer (Business Rules):</strong> What must happen invisibly to ensure consistency and compliance?</li>
            <li><strong>Data Layer (Storage & Reporting):</strong> What evidence will prove the system's success later?</li>
          </ul>
          
          <p>When these layers are mapped clearly, architecture becomes not only scalable but meaningful.</p>
          
          <h2>⚙️ Step 3 – Building Alignment Across Teams</h2>
          
          <p>Translation is never a solo act.</p>
          
          <p>As a project manager with a software-engineering background, I act as a translator in both directions — turning business intent into technical clarity and technical complexity into business insight.</p>
          
          <p>In one cross-industry project, aligning a marketing team, a data-analytics team, and backend engineers meant creating artifacts each group could consume:</p>
          
          <ul>
            <li>Journey maps for business stakeholders</li>
            <li>API sequence diagrams for developers</li>
            <li>KPI dashboards for executives</li>
          </ul>
          
          <p>When every stakeholder sees the same story told in their own language, execution flows naturally.</p>
          
          <h2>🔄 Step 4 – Designing for Change, Not Perfection</h2>
          
          <p>One of the hardest lessons in architecture is that change is inevitable. Requirements evolve, users surprise you, and systems scale in unpredictable ways.</p>
          
          <p>Instead of designing for perfection, design for adaptability. For example, in a financial-reporting platform, modularizing rule engines allowed new compliance regulations to be added without rewriting the core logic. In a supply-chain app, using configuration-driven workflows let each partner customize approvals without forking the codebase.</p>
          
          <p>If you can anticipate evolution, you can prevent disruption.</p>
          
          <h2>💬 Step 5 – Communicating the Why Back to the Team</h2>
          
          <p>Great architecture isn't only documented — it's understood.</p>
          
          <p>Every developer, tester, and analyst should know why something is built a certain way. That's where leadership meets engineering — when decisions aren't just handed down but are explained and contextualized.</p>
          
          <p>I've seen teams move faster, make fewer mistakes, and feel more ownership when they understand the reasoning behind design trade-offs — why we chose microservices over monolith, or asynchronous jobs over synchronous APIs.</p>
          
          <p>When context travels with code, quality follows.</p>
          
          <h2>🌍 The Takeaway</h2>
          
          <p>Translating business goals into technical architecture is part art, part science, and part empathy. It's about listening deeply, simplifying relentlessly, and building with intent.</p>
          
          <p>Because in the end, architecture isn't just the backbone of technology — it's the blueprint of vision. And when the blueprint is right, execution becomes poetry.</p>
          
          <h2>✨ Final Thought</h2>
          
          <p>Every project is an opportunity to bridge a gap — between vision and reality, between people and platforms, between business and technology. That bridge is where I love to stand.</p>
        </div>
      `
    },
    'software-engineer-to-project-manager': {
      icon: '🚀',
      date: 'Oct 12, 2025',
      readTime: '7 min read',
      author: 'Itrat Zehra Jassani — Senior Project Manager | Software Engineer | Product & Digital Transformation Leader',
      title: 'From Software Engineer to Project Manager — My Career Transition Story',
      content: `
        <div class="blog-content-wrapper">
          <h2>🌱 Every Journey Begins with Curiosity</h2>
          
          <p>If you had told me a few years ago that I'd be leading multiple teams, defining roadmaps, and spending more time in sprint reviews than debugging sessions, I might have laughed. I loved writing code. I loved solving problems. And I still do.</p>
          
          <p>But somewhere along the way, I realized that the most complex systems aren't always written in code — they're built in people, communication, and alignment.</p>
          
          <p>That realization changed everything.</p>
          
          <h2>🧩 The Turning Point</h2>
          
          <p>The shift began quietly. I was a developer — focused, precise, always pushing for cleaner code and better logic. Yet I often found myself doing something "extra" — writing detailed documentation, helping QA test edge cases, or explaining technical dependencies to the business team.</p>
          
          <p>At first, I didn't see it as management — I saw it as ownership. But over time, I noticed something important: projects didn't fail because of poor technology — they failed because of poor translation. Between business goals and implementation, something would get lost in the middle.</p>
          
          <p>So I stepped into that space — not because I wanted a new title, but because I wanted to close that gap.</p>
          
          <h2>⚙️ Learning to Let Go of Control</h2>
          
          <p>As a software engineer, your comfort zone is clarity. You can refactor, test, or rewrite — the code obeys logic. But as a project manager, clarity is something you create with others.</p>
          
          <p>That transition was humbling. Suddenly, my success was measured not by how elegant my code was, but by how effectively my team could deliver, collaborate, and stay motivated.</p>
          
          <p>I had to learn:</p>
          
          <ul>
            <li>To trust others with implementation details.</li>
            <li>To ask better questions instead of giving immediate answers.</li>
            <li>To measure progress in outcomes, not just in commits.</li>
            <li>And the hardest part — to stop fixing everything myself.</li>
          </ul>
          
          <h2>💡 The Leadership Mindset Shift</h2>
          
          <p>What surprised me most was that project management isn't "less technical." It's more strategic. You're still solving problems — but the problems are larger, messier, and involve people, systems, and expectations.</p>
          
          <p>I found joy in things I never expected:</p>
          
          <ul>
            <li>Breaking complex requirements into manageable milestones.</li>
            <li>Designing workflows that made teams more productive.</li>
            <li>Seeing junior developers grow because they finally had the clarity and confidence they needed.</li>
          </ul>
          
          <p>Leadership isn't about directing — it's about designing environments where others can thrive.</p>
          
          <h2>🧠 Balancing Two Worlds</h2>
          
          <p>I never stopped being an engineer — I just changed the scale of what I engineer. Now I design systems of collaboration, architecture of accountability, and frameworks of delivery.</p>
          
          <p>When a developer says, "We need two more days for QA," I don't just hear a delay. I hear dependencies, resource allocation, sprint velocity, and potential risk — all connected like nodes in a larger graph.</p>
          
          <p>That's the engineering mindset at play — just applied to people and process.</p>
          
          <h2>� Lessons from the Transition</h2>
          
          <p>If you're an engineer considering management or leadership, here's what I've learned (and still learning):</p>
          
          <ul>
            <li><strong>Don't rush the transition — evolve into it.</strong> Leadership that comes from genuine experience lasts longer than leadership assigned by title.</li>
            <li><strong>Keep your technical roots alive.</strong> Understanding architecture, data, and development lifecycles will always make you a better PM.</li>
            <li><strong>Lead with empathy.</strong> Remember what it feels like to be in the trenches, debugging a system at 2 AM. That memory keeps your leadership human.</li>
            <li><strong>Learn the language of business.</strong> Code builds products, but context builds companies. The moment you can explain a feature in terms of ROI, impact, and user value — you've elevated your craft.</li>
            <li><strong>Celebrate the wins you can't measure in lines of code.</strong> Sometimes, progress looks like a calm sprint review, a team that trusts each other, or a stakeholder who finally sees the value of your roadmap.</li>
          </ul>
          
          <h2>🌍 Full Circle</h2>
          
          <p>I didn't leave engineering. I expanded it. I still see the world in systems, dependencies, and data flows — but now I also see them in conversations, deadlines, and team dynamics.</p>
          
          <p>The truth is, moving from software engineering to project management wasn't a career shift — it was a perspective shift. And it's one that taught me that success isn't about doing everything yourself — it's about creating the conditions where everything moves forward together.</p>
          
          <h2>✨ Final Thought</h2>
          
          <p>When I look back, I realize that every phase of my career — coding, debugging, mentoring, managing — was building toward the same thing: bridging gaps.</p>
          
          <p>Between business and technology. Between goals and execution. Between people and purpose.</p>
          
          <p>And that bridge? That's where I'm most at home.</p>
        </div>
      `
    }
  };

  const post = blogPosts[slug];

  if (!post) {
    return (
      <div className="container">
        <div className="blog-post-not-found">
          <h2>Blog post not found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="container"
    >
      {/* Back Navigation */}
      <motion.div 
        className="blog-back-nav"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <Link to="/blog" className="back-to-blog">
          <FaArrowLeft /> Back to All Articles
        </Link>
      </motion.div>

      <div className="blog-post">
        <div className="blog-post-header">
          <div className="blog-post-icon">{post.icon}</div>
          <div className="blog-post-meta">
            <span className="blog-post-date">
              <FaCalendarAlt /> {post.date}
            </span>
            <span>•</span>
            <span className="blog-post-read-time">
              <FaClock /> {post.readTime}
            </span>
          </div>
          <h1 className="blog-post-title">{post.title}</h1>
          <p className="blog-post-author">by {post.author}</p>
        </div>

        {post.heroImage && (
          <div className="blog-post-hero">
            <img
              src={post.heroImage}
              alt={post.heroAlt}
              width="1599"
              height="900"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        )}
        
        <div 
          className="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        
        <div className="blog-post-footer">
          <div className="blog-post-tags">
            {slug === 'will-ai-replace-software-developers' && (
              <>
                <span className="tag">AI and Developers</span>
                <span className="tag">Software Engineering</span>
                <span className="tag">Developer Experience</span>
                <span className="tag">Technical Leadership</span>
              </>
            )}
            {/* Temporarily hidden until the article is ready to publish.
            {slug === 'automation-vs-ai-2026' && (
              <>
                <span className="tag">AI & Machine Learning</span>
                <span className="tag">Automation</span>
                <span className="tag">Technical Strategy</span>
                <span className="tag">Decision Frameworks</span>
              </>
            )}
            */}
            {slug === 'business-goals-technical-architecture' && (
              <>
                <span className="tag">Project Management</span>
                <span className="tag">Technical Architecture</span>
                <span className="tag">Leadership</span>
                <span className="tag">Business Strategy</span>
              </>
            )}
            {slug === 'software-engineer-to-project-manager' && (
              <>
                <span className="tag">Career Development</span>
                <span className="tag">Leadership</span>
                <span className="tag">Engineering Management</span>
                <span className="tag">Professional Growth</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* More Blog Posts Section */}
      {otherBlogPosts.length > 0 && (
        <motion.div 
          className="more-blogs-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="more-blogs-title">More Articles</h2>
          <div className="more-blogs-grid">
            {otherBlogPosts.map((blogPost, index) => (
              <motion.div
                key={blogPost.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              >
                <Link to={`/blog/${blogPost.id}`} className="more-blog-card">
                  <div className="more-blog-icon">{blogPost.icon}</div>
                  <div className="more-blog-content">
                    <div className="more-blog-meta">
                      <span>{blogPost.date}</span>
                      <span>•</span>
                      <span>{blogPost.readTime}</span>
                    </div>
                    <h3>{blogPost.title}</h3>
                    <p>{blogPost.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="view-all-blogs">
            <Link to="/blog" className="view-all-btn">
              View All Articles →
            </Link>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default BlogPost;
